<template>
  <div class="camera-annotation">
    <!-- 左侧面板 -->
    <div class="left-panel">
      <!-- 区域树 -->
      <div class="panel-section region-section">
        <div class="section-header">
          <span class="section-title">区域列表</span>
          <div class="tree-actions">
            <el-button type="text" size="mini" @click="expandAllNodes">展开全部</el-button>
            <el-button type="text" size="mini" @click="collapseAllNodes">折叠全部</el-button>
          </div>
        </div>
        <el-input
          v-model="regionFilter"
          placeholder="搜索区域..."
          size="small"
          prefix-icon="el-icon-search"
          clearable
        />
        <el-tree
          ref="regionTree"
          :data="regionTree"
          :props="treeProps"
          :filter-node-method="filterNode"
          node-key="indexCode"
          highlight-current
          default-expand-all
          :expand-on-click-node="false"
          @node-click="onRegionClick"
          class="region-tree"
        />
      </div>
      <!-- 摄像头列表 -->
      <div class="panel-section camera-section">
        <div class="section-header">
          <span class="section-title">摄像头列表</span>
          <span v-if="selectedRegion" class="region-label">{{ selectedRegion.name }}</span>
        </div>
        <el-input
          v-model="cameraFilter"
          placeholder="搜索摄像头..."
          size="small"
          prefix-icon="el-icon-search"
          clearable
        />
        <div class="camera-list" v-loading="cameraLoading">
          <el-tooltip
            v-for="camera in filteredCameras"
            :key="camera.indexCode"
            placement="right"
            effect="dark"
            :content="camera.regionPathName || ''"
            :disabled="!camera.regionPathName"
          >
            <div
              class="camera-item"
              :class="{
                'is-annotated': isAnnotated(camera.indexCode),
                'is-dragging': draggingCamera && draggingCamera.indexCode === camera.indexCode
              }"
              draggable="true"
              @dragstart="onDragStart(camera, $event)"
              @dragend="onDragEnd"
            >
              <div class="camera-icon">📷</div>
              <div class="camera-info">
                <div class="camera-name">{{ camera.name }}</div>
                <div class="camera-location" v-if="camera.installLocation">{{ camera.installLocation }}</div>
              </div>
              <el-tag v-if="isAnnotated(camera.indexCode)" size="mini" type="success" class="annotated-tag">已标注</el-tag>
            </div>
          </el-tooltip>
          <div v-if="!cameraLoading && cameras.length === 0" class="empty-hint">
            请先在左侧选择区域
          </div>
        </div>
      </div>
    </div>
    <!-- 右侧地图面板 -->
    <div
      class="map-panel"
      ref="mapWrapper"
      @contextmenu.prevent
      @drop.prevent="onMapDrop"
      @dragover.prevent="onMapDragOver"
      @dragleave="onMapDragLeave"
      :class="{ 'drag-over': isDragOverMap }"
    >
      <div id="annotationMapContainer" class="map-container"></div>
      <div class="map-toolbar">
        <div class="toolbar-group">
          <el-select v-model="mapLayer" @change="switchMapLayer" size="mini" style="width: 80px">
            <el-option label="标准" value="standard"></el-option>
            <el-option label="卫星" value="satellite"></el-option>
          </el-select>
        </div>
        <div class="toolbar-divider"></div>
        <el-switch
          v-model="showDeviceNames"
          @change="onDeviceNamesToggle"
          active-text="显示名称"
          inactive-text="隐藏名称"
          size="small"
        ></el-switch>
      </div>
      <div v-if="isDragOverMap" class="drop-hint">松开鼠标放置摄像头</div>
      <!-- 右键菜单 -->
      <div
        v-show="contextMenuVisible"
        class="context-menu"
        :style="{ left: contextMenuX + 'px', top: contextMenuY + 'px' }"
        @contextmenu.stop.prevent
      >
        <div class="context-menu-item" @click="editPosition">
          <i class="el-icon-edit"></i>
          编辑位置
        </div>
        <div class="context-menu-item danger" @click="deleteAnnotation">
          <i class="el-icon-delete"></i>
          删除标注
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getRegionTree,
  getCameraList,
  saveAnnotation,
  getAnnotationList,
  removeAnnotation
} from './api/index'

export default {
  name: 'CameraAnnotation',
  data() {
    return {
      // 区域树
      regionTree: [],
      treeProps: { children: 'children', label: 'name' },
      selectedRegion: null,
      regionFilter: '',
      // 摄像头列表
      cameras: [],
      cameraFilter: '',
      cameraLoading: false,
      draggingCamera: null,
      // 地图
      map: null,
      mapReady: false,
      isDragOverMap: false,
      mapLayer: 'standard',
      satelliteLayer: null,
      roadNetLayer: null,
      defaultLayers: null,
      // 标注数据 { indexCode: annotationData }
      annotationMap: {},
      // 地图标记 { indexCode: AMap.Marker }
      markers: {},
      // 右键菜单
      contextMenuVisible: false,
      contextMenuX: 0,
      contextMenuY: 0,
      contextMenuIndexCode: null,
      // 编辑模式
      editingIndexCode: null,
      // 显示设备名称
      showDeviceNames: true,
      // 防抖
      debounceTimer: null
    }
  },
  computed: {
    filteredCameras() {
      if (!this.cameraFilter) return this.cameras
      const keyword = this.cameraFilter.toLowerCase()
      return this.cameras.filter(c =>
        c.name.toLowerCase().includes(keyword) ||
        (c.installLocation && c.installLocation.toLowerCase().includes(keyword))
      )
    }
  },
  watch: {
    cameraFilter() {
      if (this.debounceTimer) clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => {
        this.loadCameras()
      }, 300)
    },
    regionFilter(val) {
      this.$refs.regionTree && this.$refs.regionTree.filter(val)
    }
  },
  mounted() {
    this.initMap()
    this.loadRegionTree()
    this.loadAnnotations()
    // 全局点击隐藏右键菜单
    document.addEventListener('click', this.onDocumentClick)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onDocumentClick)
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
  },
  methods: {
    // ==================== 地图初始化 ====================
    initMap() {
      this.map = new AMap.Map('annotationMapContainer', {
        resizeEnable: true,
        zoom: 15,
        zooms: [3, 22],
        jogEnable: false,
        center: [120.478107, 36.118357], // 崂山校区
        mapStyle: 'amap://styles/light'
      })
      // 保存默认图层组，用于切回标准模式时恢复
      this.defaultLayers = this.map.getLayers()
      // 预先创建卫星图层和道路网图层，后续切换用
      this.satelliteLayer = new AMap.TileLayer.Satellite()
      this.roadNetLayer = new AMap.TileLayer.RoadNet()
      // 地图点击隐藏右键菜单
      this.map.on('click', () => {
        this.hideContextMenu()
        if (this.editingIndexCode) {
          this.cancelEdit()
        }
      })
      this.mapReady = true
    },

    // ==================== 区域树 ====================
    loadRegionTree() {
      getRegionTree().then(res => {
        this.regionTree = res.data || []
      }).catch(() => {
        this.$message.error('加载区域树失败')
      })
    },
    filterNode(value, data) {
      if (!value) return true
      return data.name.toLowerCase().includes(value.toLowerCase())
    },
    onRegionClick(nodeData) {
      this.selectedRegion = nodeData
      this.loadCameras()
    },

    // ==================== 摄像头列表 ====================
    loadCameras() {
      if (!this.selectedRegion) return
      this.cameraLoading = true
      getCameraList(this.selectedRegion.indexCode, this.cameraFilter || undefined)
        .then(res => {
          this.cameras = res.data || []
        })
        .catch(() => {
          this.$message.error('加载摄像头列表失败')
          this.cameras = []
        })
        .finally(() => {
          this.cameraLoading = false
        })
    },

    // ==================== 标注数据加载 ====================
    loadAnnotations() {
      getAnnotationList('camera').then(res => {
        const list = res.data || []
        const map = {}
        list.forEach(item => {
          map[item.indexCode] = item
        })
        this.annotationMap = map
        // 等地图准备就绪后再创建标注点
        if (this.mapReady) {
          this.renderAllMarkers()
        }
      }).catch(() => {
        this.$message.error('加载已标注设备失败')
      })
    },
    renderAllMarkers() {
      Object.values(this.annotationMap).forEach(item => {
        this.addMarker(item)
      })
    },

    // ==================== 拖拽交互 ====================
    onDragStart(camera, event) {
      this.draggingCamera = camera
      event.dataTransfer.effectAllowed = 'copy'
      event.dataTransfer.setData('text/plain', camera.name)
    },
    onDragEnd() {
      this.draggingCamera = null
    },
    onMapDragOver(event) {
      this.isDragOverMap = true
      event.dataTransfer.dropEffect = 'copy'
    },
    onMapDragLeave() {
      this.isDragOverMap = false
    },
    onMapDrop(event) {
      this.isDragOverMap = false
      if (!this.draggingCamera) return
      const camera = this.draggingCamera
      this.draggingCamera = null
      // 计算地图坐标
      const mapRect = this.$refs.mapWrapper.getBoundingClientRect()
      const pixel = new AMap.Pixel(
        event.clientX - mapRect.left,
        event.clientY - mapRect.top
      )
      const lnglat = this.map.containerToLngLat(pixel)
      // 已有标注则更新，否则新增
      const existing = this.annotationMap[camera.indexCode]
      const saveData = {
        id: existing ? existing.id : null,
        type: 'camera',
        indexCode: camera.indexCode,
        name: camera.name,
        regionName: this.selectedRegion ? this.selectedRegion.name : '',
        lat: lnglat.lat.toFixed(6),
        lng: lnglat.lng.toFixed(6)
      }
      saveAnnotation(saveData).then(res => {
        const result = res.data
        this.annotationMap[camera.indexCode] = result
        this.addOrUpdateMarker(result)
        // 自动进入编辑模式，方便用户微调位置
        this.$nextTick(() => {
          this.enterEditMode(camera.indexCode, '拖拽标记调整位置，点击空白处确认')
        })
      }).catch(() => {
        this.$message.error('保存标注失败')
      })
    },

    // ==================== 地图标记管理 ====================
    createMarkerContent(annotation, isEditing) {
      const div = document.createElement('div')
      div.className = 'camera-marker'
      div.style.cssText = `
        width: 36px;
        height: 36px;
        background: ${isEditing ? '#e6a23c' : '#409eff'};
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        border: 3px solid #fff;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: grab;
        transition: all 0.2s;
      `
      const inner = document.createElement('span')
      inner.style.cssText = 'transform: rotate(45deg); font-size: 16px; line-height: 1;'
      inner.textContent = '📷'
      div.appendChild(inner)
      // 直接绑定右键菜单（保证只绑定一次，后续setContent不会再覆盖）
      div.addEventListener('contextmenu', (e) => {
        e.preventDefault()
        e.stopPropagation()
        this.hideContextMenu()
        this.contextMenuIndexCode = annotation.indexCode
        this.contextMenuX = e.clientX
        this.contextMenuY = e.clientY
        this.$nextTick(() => {
          this.contextMenuVisible = true
        })
      })
      return div
    },
    setMarkerContent(marker, isEditing) {
      // 替换标记内容元素，同时重新绑定右键监听器
      const indexCode = marker.getExtData().indexCode
      const annotation = this.annotationMap[indexCode]
      if (!annotation) return
      const newContent = this.createMarkerContent(annotation, isEditing)
      marker.setContent(newContent)
      marker.setDraggable(isEditing)
    },
    addMarker(annotation) {
      if (this.markers[annotation.indexCode]) return
      const content = this.createMarkerContent(annotation, false)
      const markerOptions = {
        position: new AMap.LngLat(
          parseFloat(annotation.lng),
          parseFloat(annotation.lat)
        ),
        content: content,
        offset: new AMap.Pixel(-18, -18),
        title: annotation.name,
        extData: { indexCode: annotation.indexCode }
      }
      // 如果开启显示名称，添加label
      if (this.showDeviceNames) {
        markerOptions.label = {
          content: '<div class="amap-marker-label">' + annotation.name + '</div>',
          offset: new AMap.Pixel(0, -30)
        }
      }
      const marker = new AMap.Marker(markerOptions)
      // 拖拽结束更新位置（不退出编辑模式，用户可继续拖拽微调）
      marker.on('dragend', () => {
        if (this.editingIndexCode === annotation.indexCode) {
          const pos = marker.getPosition()
          const data = this.annotationMap[annotation.indexCode]
          if (!data) return
          const saveData = {
            id: data.id,
            type: 'camera',
            indexCode: data.indexCode,
            name: data.name,
            regionName: data.regionName || '',
            lat: pos.lat.toFixed(6),
            lng: pos.lng.toFixed(6)
          }
          saveAnnotation(saveData).then(res => {
            this.annotationMap[data.indexCode] = res.data
            // 不退出编辑模式，用户可继续拖拽
          }).catch(() => {
            this.$message.error('更新位置失败')
          })
        }
      })
      this.map.add(marker)
      this.markers[annotation.indexCode] = marker
    },
    addOrUpdateMarker(annotation) {
      const existing = this.markers[annotation.indexCode]
      if (existing) {
        existing.setPosition(new AMap.LngLat(
          parseFloat(annotation.lng),
          parseFloat(annotation.lat)
        ))
      } else {
        this.addMarker(annotation)
      }
    },
    removeMarker(indexCode) {
      const marker = this.markers[indexCode]
      if (marker) {
        this.map.remove(marker)
        delete this.markers[indexCode]
      }
      delete this.annotationMap[indexCode]
    },
    isAnnotated(indexCode) {
      return !!this.annotationMap[indexCode]
    },

    // ==================== 右键菜单 ====================
    // ==================== 显示/隐藏设备名称 ====================
    onDeviceNamesToggle(val) {
      Object.values(this.markers).forEach(marker => {
        const data = this.annotationMap[marker.getExtData().indexCode]
        if (!data) return
        if (val) {
          marker.setLabel({
            content: '<div class="amap-marker-label">' + data.name + '</div>',
            offset: new AMap.Pixel(0, -30)
          })
        } else {
          marker.setLabel(null)
        }
      })
    },

    // ==================== 图层切换 ====================
    switchMapLayer(layer) {
      if (layer === 'satellite') {
        // 用 setLayers 整体替换：只保留卫星图 + 道路网，移除默认底图
        this.map.setLayers([this.satelliteLayer, this.roadNetLayer])
      } else {
        // 恢复默认图层组，并应用浅色样式
        this.map.setLayers(this.defaultLayers)
        this.map.setMapStyle('amap://styles/light')
      }
    },

    // ==================== 区域树展开/折叠 ====================
    expandAllNodes() {
      const nodesMap = this.$refs.regionTree.store.nodesMap
      if (!nodesMap) return
      Object.values(nodesMap).forEach(node => {
        if (node.childNodes && node.childNodes.length > 0 && !node.expanded) {
          node.expand()
        }
      })
    },
    collapseAllNodes() {
      const nodesMap = this.$refs.regionTree.store.nodesMap
      if (!nodesMap) return
      Object.values(nodesMap).forEach(node => {
        if (node.childNodes && node.childNodes.length > 0 && node.expanded) {
          node.collapse()
        }
      })
    },

    // ==================== 右键菜单 ====================
    onDocumentClick() {
      this.hideContextMenu()
    },
    hideContextMenu() {
      this.contextMenuVisible = false
      this.contextMenuIndexCode = null
    },

    // ==================== 编辑位置 ====================
    enterEditMode(indexCode, hintMsg) {
      if (!indexCode) return
      // 如果正在编辑其他标记，先取消
      if (this.editingIndexCode && this.editingIndexCode !== indexCode) {
        this.cancelEdit()
      }
      const marker = this.markers[indexCode]
      if (!marker) return
      this.editingIndexCode = indexCode
      this.setMarkerContent(marker, true)
      if (hintMsg) {
        this.$message.info(hintMsg)
      }
    },
    editPosition() {
      const indexCode = this.contextMenuIndexCode
      if (!indexCode) return
      this.hideContextMenu()
      this.enterEditMode(indexCode, '请拖拽标记到新位置，点击空白处确认')
    },
    cancelEdit() {
      if (this.editingIndexCode) {
        const marker = this.markers[this.editingIndexCode]
        if (marker) {
          this.setMarkerContent(marker, false)
        }
        this.editingIndexCode = null
      }
    },

    // ==================== 删除标注 ====================
    deleteAnnotation() {
      const indexCode = this.contextMenuIndexCode
      if (!indexCode) return
      const annotation = this.annotationMap[indexCode]
      if (!annotation) return
      this.hideContextMenu()
      this.$confirm('确定删除该摄像头标注吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        removeAnnotation(annotation.id).then(() => {
          this.removeMarker(indexCode)
          this.$message.success('删除成功')
        }).catch(() => {
          this.$message.error('删除失败')
        })
      }).catch(() => {})
    }
  }
}
</script>

<style scoped lang="scss">
.camera-annotation {
  display: flex;
  height: 100%;
  position: relative;
  background: #f5f7fa;
  margin-left: 38px;
}

// ==================== 左侧面板 ====================
.left-panel {
  width: 370px;
  min-width: 370px;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-right: 1px solid #e4e7ed;
  z-index: 10;
}

.panel-section {
  display: flex;
  flex-direction: column;
  padding: 12px;

  .section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;

    .section-title {
      font-size: 14px;
      font-weight: 700;
      color: #303133;
    }
    .region-label {
      font-size: 12px;
      color: #909399;
      max-width: 150px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .el-input {
    margin-bottom: 8px;
  }
}

.region-section {
  flex-shrink: 0;
  max-height: 45%;
  overflow: hidden;

  .region-tree {
    flex: 1;
    overflow-y: auto;
  }
}

.camera-section {
  flex: 1;
  overflow: hidden;
  border-top: 1px solid #ebeef5;

  .camera-list {
    flex: 1;
    overflow-y: auto;
    min-height: 0;
  }
}

// ==================== 摄像头列表项 ====================
.camera-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: grab;
  transition: all 0.2s;
  border: 1px solid transparent;
  margin-bottom: 4px;

  &:hover {
    background: #ecf5ff;
    border-color: #d9ecff;
  }

  &:active {
    cursor: grabbing;
  }

  &.is-annotated {
    background: #f0f9eb;
    border-color: #e1f3d8;
  }

  &.is-dragging {
    opacity: 0.5;
  }

  .camera-icon {
    font-size: 20px;
    margin-right: 10px;
    flex-shrink: 0;
  }

  .camera-info {
    flex: 1;
    min-width: 0;

    .camera-name {
      font-size: 13px;
      color: #303133;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .camera-location {
      font-size: 11px;
      color: #909399;
      margin-top: 2px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .annotated-tag {
    flex-shrink: 0;
    margin-left: 6px;
  }
}

.empty-hint {
  text-align: center;
  color: #c0c4cc;
  padding: 30px 0;
  font-size: 13px;
}

// ==================== 右侧地图面板 ====================
.map-panel {
  flex: 1;
  position: relative;
  overflow: hidden;
  transition: all 0.2s;

  &.drag-over {
    box-shadow: inset 0 0 20px rgba(64, 158, 255, 0.4);
  }

  .map-container {
    width: 100%;
    height: 100%;
  }

  .map-toolbar {
    position: absolute;
    top: 12px;
    left: 12px;
    z-index: 1000;
    background: rgba(255, 255, 255, 0.95);
    border-radius: 6px;
    padding: 6px 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    display: flex;
    align-items: center;
    gap: 8px;

    .toolbar-group {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .toolbar-label {
      font-size: 12px;
      color: #606266;
      white-space: nowrap;
    }

    .toolbar-divider {
      width: 1px;
      height: 20px;
      background: #dcdfe6;
    }
  }

  .drop-hint {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    padding: 16px 32px;
    background: rgba(64, 158, 255, 0.9);
    color: #fff;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 700;
    pointer-events: none;
    z-index: 1000;
    box-shadow: 0 4px 16px rgba(64, 158, 255, 0.4);
  }
}

// ==================== 区域树操作按钮 ====================
.tree-actions {
  display: flex;
  gap: 0;
  .el-button {
    padding: 0 6px;
    font-size: 12px;
    color: #409eff;
    &:hover {
      color: #66b1ff;
    }
  }
}

// ==================== 右键菜单 ====================

.context-menu {
  position: fixed;
  z-index: 9999;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  padding: 4px 0;
  min-width: 130px;

  .context-menu-item {
    padding: 8px 16px;
    font-size: 13px;
    color: #303133;
    cursor: pointer;
    display: flex;
    align-items: center;
    transition: background 0.15s;

    i {
      margin-right: 6px;
      font-size: 14px;
    }

    &:hover {
      background: #ecf5ff;
    }

    &.danger {
      color: #f56c6c;

      &:hover {
        background: #fef0f0;
      }
    }
  }
}
</style>

<!-- 非scoped样式：AMap渲染的DOM不在Vue作用域内，需全局样式 -->
<style lang="scss">
.amap-marker-label {
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  pointer-events: none;
  user-select: none;
}
</style>
